import api from "./api";

const SDK_URL = "https://checkout.razorpay.com/v1/checkout.js";
let sdkPromise = null;

export function loadRazorpaySdk() {
    if (typeof window === "undefined") return Promise.resolve(false);
    if (window.Razorpay) return Promise.resolve(true);
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise((resolve) => {
        const s = document.createElement("script");
        s.src = SDK_URL;
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => { sdkPromise = null; resolve(false); };
        document.body.appendChild(s);
    });
    return sdkPromise;
}

export async function fetchRazorpayConfig() {
    try {
        const { data } = await api.get("/payments/razorpay/config");
        return data;
    } catch {
        return { enabled: false, key_id: "", mode: null };
    }
}

/**
 * Opens Razorpay Checkout for a server-computed amount and resolves with the
 * created order/appointment once the payment is verified server-side.
 * Rejects with `err.cancelled = true` when the shopper closes the popup.
 */
export async function payWithRazorpay({ purpose, order = null, appointment = null }) {
    const loaded = await loadRazorpaySdk();
    if (!loaded) throw new Error("Could not reach the payment gateway. Check your connection and retry.");

    const { data: intent } = await api.post("/payments/razorpay/intent", { purpose, order, appointment });

    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (fn, val) => { if (!settled) { settled = true; fn(val); } };
        const release = (reason) => {
            api.post("/payments/razorpay/abandon", { intent_id: intent.intent_id, reason }).catch(() => { });
        };

        const rzp = new window.Razorpay({
            key: intent.key_id,
            amount: intent.amount,
            currency: intent.currency,
            order_id: intent.razorpay_order_id,
            name: intent.name,
            description: intent.description,
            prefill: intent.prefill,
            notes: { intent_id: intent.intent_id },
            theme: { color: "#0369a1" },
            handler: async (res) => {
                try {
                    const { data } = await api.post("/payments/razorpay/confirm", {
                        intent_id: intent.intent_id,
                        razorpay_order_id: res.razorpay_order_id,
                        razorpay_payment_id: res.razorpay_payment_id,
                        razorpay_signature: res.razorpay_signature,
                    });
                    finish(resolve, data);
                } catch (e) {
                    finish(reject, new Error(e.response?.data?.detail || "Payment captured but confirmation failed. Contact support with your payment id."));
                }
            },
            modal: {
                ondismiss: () => {
                    release("dismissed");
                    const err = new Error("Payment cancelled — your order was not placed.");
                    err.cancelled = true;
                    finish(reject, err);
                },
            },
        });

        rzp.on("payment.failed", (resp) => {
            release(resp?.error?.description || "payment_failed");
            finish(reject, new Error(resp?.error?.description || "Payment failed. No order was created."));
        });

        rzp.open();
    });
}
